import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Home, GraduationCap, Plane, Heart, Briefcase, Sun, ArrowRight } from "lucide-react";
import { GOAL_CARDS } from "@/constants/testIds";

const goals = [
  { icon: Sun, t: "Retire in peace", d: "A 25-year SIP turns ₹15k/month into a corpus that pays for life after work.", img: "https://images.pexels.com/photos/3768146/pexels-photo-3768146.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { icon: GraduationCap, t: "Fund your child's dream", d: "Plan 15 years ahead — give them a choice, not a constraint.", img: "https://images.pexels.com/photos/8617715/pexels-photo-8617715.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { icon: Home, t: "Own your home", d: "Build the down payment without dipping into the emergency fund.", img: "https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { icon: Plane, t: "Take the dream sabbatical", d: "Yes, vacations can be a goal too. Plan it. Earn it. Take it.", img: "https://images.pexels.com/photos/1271619/pexels-photo-1271619.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { icon: Heart, t: "Care for your parents", d: "A dedicated portfolio so love is never a financial trade-off.", img: "https://images.pexels.com/photos/3768131/pexels-photo-3768131.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { icon: Briefcase, t: "Start your own thing", d: "An 18–24 month runway so you can finally bet on yourself.", img: "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=600" },
];

export default function GoalCards() {
  return (
    <section
      data-testid={GOAL_CARDS.section}
      id="goals"
      className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32"
    >
      <div className="grid md:grid-cols-12 gap-10 mb-14">
        <div className="md:col-span-7">
          <span className="text-xs uppercase tracking-[0.25em] text-secondary font-medium">Goals We Reach Together</span>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-primary mt-3 leading-[1.1]">
            Money has no meaning <em className="not-italic text-secondary">without a purpose.</em>
          </h2>
        </div>
        <div className="md:col-span-5 md:pt-4">
          <p className="text-lg text-foreground/75 leading-relaxed">
            We don't sell products — we plan towards what matters to you. Pick a goal and we'll show you the road.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {goals.map((g, i) => (
          <motion.article
            key={g.t}
            data-testid={GOAL_CARDS.card}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.06, duration: 0.5 }}
            className="group relative rounded-3xl overflow-hidden border border-border bg-card hover:-translate-y-1 transition-all duration-300 hover:shadow-lg"
          >
            <div className="relative h-44 overflow-hidden">
              <img src={g.img} alt={g.t} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent"></div>
              <div className="absolute top-4 left-4 w-10 h-10 rounded-full bg-background/90 backdrop-blur grid place-items-center text-primary">
                <g.icon className="w-4 h-4" />
              </div>
            </div>
            <div className="p-6">
              <h3 className="font-serif text-xl text-primary">{g.t}</h3>
              <p className="text-sm text-foreground/70 mt-2 leading-relaxed">{g.d}</p>
              <Link to="/calculators" className="mt-4 inline-flex items-center gap-1 text-sm text-secondary font-medium group-hover:gap-2 transition-all">
                Plan this goal <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
